/**
 * Online visit "top-up first": the pay button charges the wallet (Katibe topups) without
 * booking; booking runs after a successful return and is paid from the wallet.
 * Booking services are slow — this moves that wait after payment.
 */

/** GrowthBook feature (targeted by user id) — enables top-up-first on the online visit factor. */
export const TOPUP_FIRST_FEATURE = 'online-visit-topup-first';

/** payment_method that pays the booking from the p24 wallet balance. */
export const WALLET_PAYMENT_METHOD = 'wallet';

export const TOPUP_RETURN_PARAM = 'topup_return';
export const TOPUP_UUID_PARAM = 'topup_uuid';

const STORAGE_KEY = 'p24:topup-first';
/** A pending top-up older than this is ignored (user abandoned the gateway). */
const PENDING_TTL_MS = 60 * 60 * 1000;

export interface PendingTopup {
  uuid: string;
  centerId: string;
  serviceId: string;
  userCenterId: string;
  /** Total the booking needs from the wallet, in rial (invoice incl. tax, after discount). */
  requiredRial: number;
  discountCode?: string;
  createdAt: number;
  /** Set once booking has been attempted, so a reload never books twice. */
  bookingStarted?: boolean;
}

/**
 * Rial sent to Katibe topups: the whole invoice (service price + tax − discount) plus any
 * optional extra top-up. Never subtract the wallet balance — topups itself decides how much
 * of it to use and how much to charge.
 */
export function computeTopupAmountRial(totalRial: number, extraRial = 0): number {
  const total = Math.max(0, Math.floor(Number(totalRial) || 0));
  const extra = Math.max(0, Math.floor(Number(extraRial) || 0));
  return total + extra;
}

export function buildTopupUrl({
  amountRial,
  uuid,
  pageUrl,
  centerId,
  paymentMethod,
  taxRial,
  discountRial,
}: {
  amountRial: number;
  uuid: string;
  /** The factor page URL to come back to. */
  pageUrl: string;
  /** Same params the legacy booking payment sends to Katibe topups (center 5532 = personal p24 wallet). */
  centerId?: string;
  paymentMethod?: string;
  taxRial?: number;
  discountRial?: number;
}): string {
  const returnUrl = (status: 'success' | 'cancel') => {
    const u = new URL(pageUrl);
    u.searchParams.set(TOPUP_RETURN_PARAM, status);
    u.searchParams.set(TOPUP_UUID_PARAM, uuid);
    return u.toString();
  };
  return `https://apigw.paziresh24.com/katibe/v1/topups?amount=${amountRial}&returnlink=${encodeURIComponent(
    returnUrl('success'),
  )}&uuid=${uuid}&cancel_returnlink=${encodeURIComponent(returnUrl('cancel'))}${centerId ? `&center_id=${centerId}` : ''}${
    paymentMethod ? `&payment_method=${paymentMethod}` : ''
  }&discount_amount=${Math.max(0, Math.floor(discountRial || 0))}&tax_amount=${Math.max(0, Math.floor(taxRial || 0))}`;
}

/** Page URL with the top-up return params removed. */
export function stripTopupParams(url: string): string {
  const u = new URL(url);
  u.searchParams.delete(TOPUP_RETURN_PARAM);
  u.searchParams.delete(TOPUP_UUID_PARAM);
  return u.toString();
}

export function savePendingTopup(pending: PendingTopup) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable — the return page will show the "money is in your wallet" state.
  }
}

export function loadPendingTopup(uuid: string, now = Date.now()): PendingTopup | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingTopup;
    if (pending?.uuid !== uuid) return null;
    if (now - pending.createdAt > PENDING_TTL_MS) return null;
    return pending;
  } catch {
    return null;
  }
}

export function clearPendingTopup() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
